import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "@/lib/api";
import { CATEGORIES } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import ArticleCard from "@/components/ArticleCard";
import { Skeleton } from "@/components/ui/skeleton";

const Articles = () => {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "";
  const q = params.get("q") || "";
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(q);

  useEffect(() => {
    setLoading(true);
    const p = {};
    if (category) p.category = category;
    if (q) p.q = q;
    api.get("/articles", { params: p }).then((r) => setArticles(r.data)).finally(() => setLoading(false));
  }, [category, q]);

  const setCategory = (c) => {
    const np = {};
    if (c) np.category = c;
    setParams(np);
  };

  const submitSearch = (e) => {
    e.preventDefault();
    const np = {};
    if (search.trim()) np.q = search.trim();
    setParams(np);
  };

  return (
    <div data-testid="articles-page">
      <section className="bg-navy text-white">
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 grain opacity-40" />
          <div className="relative mx-auto max-w-7xl px-5 lg:px-8 py-16">
            <span className="overline-label text-electric">Content Platform</span>
            <h1 className="mt-3 font-display text-4xl lg:text-5xl font-extrabold">
              {category || (q ? `Search: "${q}"` : "Insights & Analysis")}
            </h1>
            <p className="mt-4 text-white/70 max-w-2xl">
              Technology and business journalism, research-backed and written for enterprise decision-makers.
            </p>
            <form onSubmit={submitSearch} className="mt-7 relative max-w-md">
              <Search className="absolute left-3 top-3 h-4 w-4 text-navy/50" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search articles..." className="pl-9 h-11 bg-white text-navy"
                data-testid="articles-search-input" />
            </form>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-5 lg:px-8 py-10">
        <div className="flex flex-wrap gap-2.5 mb-10">
          <button onClick={() => setCategory("")}
            className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${!category ? "bg-navy text-white border-navy" : "bg-white text-navy border-border hover:border-navy"}`}
            data-testid="filter-all">
            All
          </button>
          {CATEGORIES.map((c) => (
            <button key={c} onClick={() => setCategory(c)}
              className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${category === c ? "bg-navy text-white border-navy" : "bg-white text-navy border-border hover:border-navy"}`}
              data-testid={`filter-${c.replace(/\s|&/g, "-").toLowerCase()}`}>
              {c}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-80 rounded-xl" />)}
          </div>
        ) : articles.length === 0 ? (
          <div className="py-24 text-center" data-testid="articles-empty">
            <p className="text-muted-foreground">No articles found. <Link to="/articles" className="text-electric font-medium">View all</Link></p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="articles-grid">
            {articles.map((a, i) => <ArticleCard key={a.id} article={a} index={i} />)}
          </div>
        )}
      </div>
    </div>
  );
};

export default Articles;
